import React, { useMemo } from 'react';
import NextLink from 'next/link';
import { useParams } from 'next/navigation';
import useClassNames from 'helpers/hooks/useClassNames';
import { resolveReferenceProps, resolveReferenceTarget } from 'helpers/reference';
import { i18nConfig } from 'project.config';
import { Reference } from 'types/reference';
import { ButtonSize, ButtonVariant } from '../button';
import useButtonClassNames from '../button/useButtonClassNames';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

export type LinkVariant = 'primary' | 'menu-header' | 'menu-item' | 'breadcrumb' | 'nav-link-subcategory-item';

export interface LinkProps extends Omit<React.ComponentProps<typeof NextLink>, 'href' | 'key'> {
  link?: string | Reference;
  variant?: LinkVariant;
  showExternalIcon?: boolean;
  buttonVariant?: ButtonVariant;
  buttonSize?: ButtonSize;
}

type VariantStyle = { [key in LinkVariant]: string };

const variantStyle: VariantStyle = {
  primary: 'hyperlink cursor-pointer text-link hover:text-blue-dark underline font-bold leading-tight tracking-[0.4%]',
  'menu-item': 'text-secondary-black hover:underline hover:underline-offset-2',
  breadcrumb: 'text-14 font-medium text-primary-black',
  'menu-header': 'text-14 font-medium text-primary-black cursor-pointer',
  'nav-link-subcategory-item': 'body-text-small font-normal hover:font-medium',
};

const Link = ({
  link,
  children,
  className = '',
  showExternalIcon,
  variant,
  buttonVariant,
  buttonSize = 's',
  title = '',
  locale: localeProp,
  ...props
}: React.PropsWithChildren<LinkProps>) => {
  const linkUrl = useMemo(() => {
    if (!link) return '';
    if (typeof link === 'string') return link;
    return resolveReferenceTarget(link);
  }, [link]);

  const linkProps = useMemo(() => {
    if (!link || typeof link === 'string') return {};
    return resolveReferenceProps(link);
  }, [link]);

  const isExternal = linkUrl?.startsWith('http');
  const useExternalIcon = isExternal && showExternalIcon;

  const buttonClassName = useButtonClassNames({
    variant: buttonVariant,
    size: buttonSize,
    className: '',
    includesIcon: false,
    loading: false,
    asSkeleton: false,
  });

  const computedClassName = buttonVariant
    ? buttonClassName
    : variant
      ? `${useExternalIcon ? 'inline-flex items-center gap-x-8' : ''} ${variantStyle[variant]}`
      : '';

  const linkClassNames = useClassNames([computedClassName, className]);

  return (
    <NextLink {...props} {...linkProps} href={linkUrl ?? '#'} className={linkClassNames} title={title}>
      {children}
      {useExternalIcon && <ArrowTopRightOnSquareIcon className="size-24" />}
    </NextLink>
  );
};

export default Link;
